import { Icon } from '../Icon';
import { suggestBehaviorFromPeer, gradeLabelKo } from '../../lib/peerCalibrationLink';
import type { Member, PeerReportSummary } from '../../types';

interface CalibrationModalProps {
    selectedMemberForCalibration: Member;
    onClose: () => void;
    peerReportByMember: Record<string, PeerReportSummary>;
    editPerfGrade: string;
    setEditPerfGrade: (v: string) => void;
    editBehaviorGrade: string;
    setEditBehaviorGrade: (v: string) => void;
    editNote: string;
    setEditNote: (v: string) => void;
    applyPeerBehaviorSuggest: () => void;
    handleSaveCalibration: () => void;
    evalYear: number;
}

export function CalibrationModal({
    selectedMemberForCalibration,
    onClose,
    peerReportByMember,
    editPerfGrade,
    setEditPerfGrade,
    editBehaviorGrade,
    setEditBehaviorGrade,
    editNote,
    setEditNote,
    applyPeerBehaviorSuggest,
    handleSaveCalibration,
    evalYear,
}: CalibrationModalProps) {
    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl relative border border-slate-100">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"
                >
                    <Icon name="x" className="w-5 h-5" />
                </button>

                <h3 className="text-base font-bold text-slate-900 mb-2 flex items-center gap-2">
                    <Icon name="edit-3" className="w-5 h-5 text-indigo-600" /> 임직원 최종 등급 조율 (Calibration)
                </h3>
                <p className="text-xs text-slate-500 mb-4">집단 논의와 다면평가(일하는 방식) 집계를 참고해 성과·방식 등급을 재배치합니다.</p>

                <div className="space-y-4 text-xs">
                    <div className="bg-slate-50 p-3 rounded-xl border border-slate-200">
                        <div className="flex justify-between items-center mb-1">
                            <span className="font-bold text-slate-800">{selectedMemberForCalibration.name} {selectedMemberForCalibration.duty} ({selectedMemberForCalibration.department})</span>
                            <span className="text-[10px] text-slate-400 bg-white border px-2 py-0.5 rounded">1차 등급: 성과 {selectedMemberForCalibration.perfGrade} / 방식 {selectedMemberForCalibration.behaviorGrade}</span>
                        </div>
                    </div>

                    {(() => {
                        const peerSuggest = suggestBehaviorFromPeer(peerReportByMember[selectedMemberForCalibration.id] as never);
                        if (!peerSuggest) {
                            return (
                                <div className="bg-amber-50 border border-amber-100 rounded-xl px-3 py-2.5 text-[11px] text-amber-800">
                                    {evalYear}년 다면평가 집계가 아직 없어 일하는 방식 추천 등급을 만들 수 없습니다.
                                </div>
                            );
                        }
                        const aligned = editBehaviorGrade === peerSuggest.grade;
                        return (
                            <div className="bg-violet-50 border border-violet-200 rounded-xl p-3 space-y-2">
                                <div className="flex justify-between items-start gap-2">
                                    <div>
                                        <p className="font-bold text-violet-900 text-xs">다면평가 → 일하는 방식 추천</p>
                                        <p className="text-[11px] text-violet-700 mt-0.5 leading-relaxed">{peerSuggest.reason}</p>
                                    </div>
                                    <span className={`shrink-0 px-2.5 py-1 rounded-lg text-[11px] font-extrabold border ${aligned ? 'bg-emerald-100 text-emerald-800 border-emerald-200' : 'bg-white text-violet-800 border-violet-200'}`}>
                                        추천 {peerSuggest.grade}
                                    </span>
                                </div>
                                <div className="grid grid-cols-3 gap-1.5 text-[10px] text-violet-800">
                                    {Object.entries(peerSuggest.byCategoryRates).map(([k, v]) => (
                                        <div key={k} className="bg-white/70 border border-violet-100 rounded-lg px-2 py-1.5">
                                            <div className="font-bold truncate">{k === 'Co-work' ? 'Co-work' : k}</div>
                                            <div>{v.rate == null ? '-' : `${v.rate}%`} · G{v.good}/B{v.bad}</div>
                                        </div>
                                    ))}
                                </div>
                                <button
                                    type="button"
                                    onClick={applyPeerBehaviorSuggest}
                                    className="w-full bg-violet-600 hover:bg-violet-700 text-white font-bold text-[11px] py-2 rounded-lg transition-all"
                                >
                                    추천 등급({gradeLabelKo(peerSuggest.grade)})을 일하는 방식에 적용
                                </button>
                            </div>
                        );
                    })()}

                    <div>
                        <label className="block text-slate-700 font-bold mb-1.5">조율 최종 성과 등급</label>
                        <div className="grid grid-cols-3 gap-2">
                            {['EX', 'GD', 'NI'].map(grade => (
                                <button
                                    key={grade}
                                    type="button"
                                    onClick={() => setEditPerfGrade(grade)}
                                    className={`py-2 rounded-lg font-extrabold border transition-all text-center ${editPerfGrade === grade ? 'bg-slate-900 text-white border-slate-900 shadow-sm' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
                                >
                                    {grade === 'EX' ? '훌륭해요 (EX)' : grade === 'GD' ? '준수해요 (GD)' : '노력해요 (NI)'}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className="block text-slate-700 font-bold mb-1.5">조율 최종 일하는 방식 등급</label>
                        <div className="grid grid-cols-3 gap-2">
                            {['EX', 'GD', 'NI'].map(grade => (
                                <button
                                    key={grade}
                                    type="button"
                                    onClick={() => setEditBehaviorGrade(grade)}
                                    className={`py-2 rounded-lg font-extrabold border transition-all text-center ${editBehaviorGrade === grade ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
                                >
                                    {grade === 'EX' ? '훌륭해요 (EX)' : grade === 'GD' ? '준수해요 (GD)' : '노력해요 (NI)'}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className="block text-slate-700 font-bold mb-1.5">등급 조율 사유 (Calibration Note)</label>
                        <textarea
                            value={editNote}
                            onChange={(e) => setEditNote(e.target.value)}
                            placeholder="집단 토의 결과를 토대로 해당 등급을 확정/조정한 객관적 사유를 명시하세요."
                            className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2.5 h-24 focus:outline-none focus:ring-2 focus:ring-indigo-500 leading-relaxed text-slate-800"
                        ></textarea>
                    </div>
                </div>

                <div className="pt-6 flex gap-3 justify-end text-xs">
                    <button
                        type="button"
                        onClick={onClose}
                        className="bg-slate-100 hover:bg-slate-200 text-slate-600 px-4 py-2 rounded-lg font-bold"
                    >
                        취소
                    </button>
                    <button
                        onClick={handleSaveCalibration}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2 rounded-lg font-bold shadow-md"
                    >
                        조율 결과 저장
                    </button>
                </div>
            </div>
        </div>
    );
}
